import { useEffect, useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { clearConnectWalk, currentConnectWalk, subscribeConnectWalk } from '../../../lib/connect-walk-popup';
import { queryClient, queryKeys } from '../../../lib/query-client';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import styles from './ConnectWalkScrim.module.css';

const CLOSED_POLL_MS = 500;
/** A walk still open after this long was left behind another window; the app comes back. */
const MAX_WALK_MS = 15 * 60_000;

/**
 * Dims the whole app while a connect walk runs in its sign-in popup (`lib/connect-walk-popup.ts`), and
 * lifts when the popup closes, or when the reader returns to this tab from a walk the browser opened as a tab.
 * A click brings the popup back to the front, which is what a reader needs when it slipped behind this
 * window; Escape lifts the dim without touching the popup.
 *
 * The provider consent pages keep `Cross-Origin-Opener-Policy` permissive for popup sign-in, so
 * `closed` stays readable across the walk. If one ever severed it, `closed` would read true early and
 * the dim would simply lift early.
 */
export function ConnectWalkScrim() {
  const walk = useSyncExternalStore(subscribeConnectWalk, currentConnectWalk, () => null);
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi('connect-walk');
  const scrimRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!walk) return;
    // The dim blocks the pointer only, so the app behind it is made inert for the keyboard and for
    // assistive tech too. Elements already inert are left as they are.
    const scrim = scrimRef.current;
    const madeInert = [...document.body.children].filter(
      (element): element is HTMLElement => element instanceof HTMLElement && element !== scrim && !element.inert,
    );
    // Usually the button that started the walk, so a keyboard reader lands back on it afterwards.
    const focusedBefore = document.activeElement;
    madeInert.forEach((element) => {
      element.inert = true;
    });
    scrim?.focus();
    const startedAt = Date.now();
    const lift = () => {
      clearConnectWalk(walk);
      void queryClient.invalidateQueries({ queryKey: queryKeys.connectors });
    };
    const timer = setInterval(() => {
      if (walk.closed || Date.now() - startedAt > MAX_WALK_MS) lift();
    }, CLOSED_POLL_MS);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') lift();
    };
    // A browser in full screen (Safari, and Chrome on a Mac) opens the "popup" as a TAB, and no window
    // feature overrides that. A real popup leaves this page visible behind it; a tab hides it. So a page that
    // went hidden during the walk and is shown again means the reader came back to this tab, where a dim they
    // cannot click through to the walk would only trap them.
    let hiddenDuringWalk = false;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') hiddenDuringWalk = true;
      else if (hiddenDuringWalk) lift();
    };
    window.addEventListener('keydown', onKeyDown);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(timer);
      window.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      madeInert.forEach((element) => {
        element.inert = false;
      });
      if (focusedBefore instanceof HTMLElement && focusedBefore.isConnected) {
        focusedBefore.focus({ preventScroll: true });
      }
    };
  }, [walk]);

  if (!walk) return null;
  const label = t('connectWalk.showWindow');
  return createPortal(
    <button
      ref={scrimRef}
      type="button"
      className={styles.scrim}
      aria-label={label}
      title={label}
      onClick={() => {
        logPageClickBi({ page_name: 'connect_walk', button_name: 'connect-walk-show-window' });
        walk.focus();
      }}
    />,
    document.body,
  );
}
